import React, { useState } from 'react';

/**
 * EverBook Infinity Loader
 *
 * A glowing orb continuously travels a figure-8 infinity path.
 * Brand-aligned palette: parchment cream variations only — luminance pulse,
 * not hue pulse. Same orb-on-figure-8 concept as before.
 *
 * Props:
 *   size      – pixel width (default 96). Height auto-scales.
 *   className – extra wrapper classes.
 *   label     – optional text below the symbol.
 */

let _idCounter = 0;

const FIGURE8 =
  'M12,12' +
  ' C14,10 16,8 18,8' +
  ' C20.2,8 22,9.8 22,12' +
  ' C22,14.2 20.2,16 18,16' +
  ' C16,16 14,14 12,12' +
  ' C10,10 8,8 6,8' +
  ' C3.8,8 2,9.8 2,12' +
  ' C2,14.2 3.8,16 6,16' +
  ' C8,16 10,14 12,12';

export const InfinityLoader = ({ size = 144, className = '', label = '' }) => {
  const [uid] = useState(() => `il${++_idCounter}`);
  const showGlow = size >= 32;
  const dur = '2.5s';

  return (
    <span
      className={`inline-flex flex-col items-center justify-center gap-2 ${className}`}
      role="status"
      aria-label="Loading"
      data-testid="infinity-loader"
    >
      <svg
        width={size}
        height={size * 0.5}
        viewBox="0 5 24 14"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{ overflow: 'visible' }}
      >
        <defs>
          <filter id={`${uid}g`} x="-30%" y="-40%" width="160%" height="180%">
            <feGaussianBlur in="SourceGraphic" stdDeviation="0.7" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <filter id={`${uid}o`} x="-400%" y="-400%" width="900%" height="900%">
            <feGaussianBlur in="SourceGraphic" stdDeviation="2" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          {/* Trail gradient: dim cream → bright cream → dim cream
              creates a luminance pulse along the trail */}
          <linearGradient id={`${uid}tg`} x1="0" y1="12" x2="24" y2="12" gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor="#a08866" />
            <stop offset="50%" stopColor="#f0e3c8" />
            <stop offset="100%" stopColor="#a08866" />
          </linearGradient>
        </defs>

        {/* Layer 1 — Base infinity line (breathing) */}
        <path
          d={FIGURE8}
          stroke="#d8c19c"
          strokeWidth="1.6"
          strokeLinecap="round"
          fill="none"
          filter={showGlow ? `url(#${uid}g)` : undefined}
        >
          <animate attributeName="opacity" values="0.15;0.35;0.15" dur="3s" repeatCount="indefinite" />
        </path>

        {/* Layer 2 — Glowing trail chasing the orb */}
        <path
          d={FIGURE8}
          pathLength="100"
          stroke={`url(#${uid}tg)`}
          strokeWidth="2"
          strokeLinecap="round"
          fill="none"
          opacity="0.7"
          strokeDasharray="25 75"
          filter={showGlow ? `url(#${uid}g)` : undefined}
        >
          <animate attributeName="stroke-dashoffset" values="25;-75" dur={dur} repeatCount="indefinite" />
        </path>

        {/* Layer 3 — Orb glow halo */}
        {showGlow && (
          <circle r="2" fill="#d8c19c" opacity="0.35" filter={`url(#${uid}o)`}>
            <animateMotion dur={dur} repeatCount="indefinite" path={FIGURE8} />
          </circle>
        )}

        {/* Layer 4 — Orb body (bright cream) */}
        <circle r="1.3" fill="#f0e3c8">
          <animateMotion dur={dur} repeatCount="indefinite" path={FIGURE8} />
        </circle>

        {/* Layer 5 — Orb bright core (near-white) */}
        <circle r="0.55" fill="#fff5e0">
          <animateMotion dur={dur} repeatCount="indefinite" path={FIGURE8} />
        </circle>
      </svg>

      {label && (
        <span className="text-xs text-stone-400 font-medium tracking-wide">{label}</span>
      )}
    </span>
  );
};
